/* Premium product card: image carousel + colour swatches + size AJAX quick-add.
   Powers <lux-card> (see snippets/lux-card.liquid). */
if (!customElements.get('lux-card')) {
  customElements.define(
    'lux-card',
    class LuxCard extends HTMLElement {
      connectedCallback() {
        try {
          this.variants = JSON.parse(this.querySelector('.lux-card__variants').textContent);
        } catch (e) {
          this.variants = [];
        }
        this.colorPos = parseInt(this.dataset.colorPos, 10) || 0; // 1-based; 0 = none
        this.sizePos = parseInt(this.dataset.sizePos, 10) || 0;

        this.imgs = Array.from(this.querySelectorAll('.lux-card__img'));
        this.current = Math.max(0, this.imgs.findIndex((i) => i.classList.contains('is-active')));
        this.swatches = Array.from(this.querySelectorAll('.lux-card__swatch'));
        this.sizeBtns = Array.from(this.querySelectorAll('.lux-card__size'));

        if (this.imgs.length <= 1) this.classList.add('lux-card--single-img');

        // Default selected colour = first swatch, else the first variant's colour.
        if (this.swatches.length) {
          const active = this.swatches.find((s) => s.classList.contains('is-active')) || this.swatches[0];
          active.classList.add('is-active');
          this.selectedColor = active.dataset.color;
        } else {
          this.selectedColor = this.optOf(this.firstVariant(), this.colorPos);
        }

        this.bind();
        this.refreshSizes();
      }

      firstVariant() {
        return this.variants.find((v) => v.available) || this.variants[0];
      }

      optOf(v, pos) {
        if (!v || !pos) return null;
        return pos === 1 ? v.o1 : pos === 2 ? v.o2 : pos === 3 ? v.o3 : null;
      }

      bind() {
        const prev = this.querySelector('.lux-card__nav--prev');
        const next = this.querySelector('.lux-card__nav--next');
        if (prev) prev.addEventListener('click', (e) => { e.preventDefault(); this.step(-1); });
        if (next) next.addEventListener('click', (e) => { e.preventDefault(); this.step(1); });

        this.swatches.forEach((sw) =>
          sw.addEventListener('click', (e) => {
            e.preventDefault();
            this.selectedColor = sw.dataset.color;
            this.swatches.forEach((s) => s.classList.toggle('is-active', s === sw));
            if (sw.dataset.media) this.showMedia(sw.dataset.media);
            this.refreshSizes();
          })
        );

        this.sizeBtns.forEach((btn) =>
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            if (btn.disabled) return;
            this.addToCart(btn);
          })
        );

        // "+" toggle (mobile): reveal / hide the size quick-add bar.
        this.toggle = this.querySelector('.lux-card__toggle');
        if (this.toggle) {
          this.toggle.addEventListener('click', (e) => {
            e.preventDefault();
            this.setSizesOpen(!this.classList.contains('lux-card--sizes-open'));
          });
        }

        this.bindSwipe();
      }

      /* Horizontal swipe/drag on the image to move through the carousel (mobile).
         A real swipe suppresses the <a>'s navigation so it doesn't open the PDP. */
      bindSwipe() {
        const media = this.querySelector('.lux-card__media');
        const link = this.querySelector('.lux-card__imglink');
        if (!media || this.imgs.length <= 1) return;

        let startX = 0, startY = 0, pid = null, dragging = false, swiped = false;

        media.addEventListener('pointerdown', (e) => {
          if (e.button && e.button !== 0) return;
          pid = e.pointerId;
          startX = e.clientX;
          startY = e.clientY;
          dragging = false;
          swiped = false;
        });
        media.addEventListener('pointermove', (e) => {
          if (pid === null || e.pointerId !== pid) return;
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          if (!dragging && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) dragging = true;
        });
        const end = (e) => {
          if (pid === null || (e.pointerId !== undefined && e.pointerId !== pid)) return;
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          pid = null;
          if (dragging && Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
            swiped = true;
            this.step(dx < 0 ? 1 : -1);
          }
          dragging = false;
        };
        media.addEventListener('pointerup', end);
        media.addEventListener('pointercancel', end);
        // If the gesture was a swipe, cancel the link click that follows it.
        if (link) {
          link.addEventListener('click', (e) => {
            if (swiped) { e.preventDefault(); swiped = false; }
          });
        }
      }

      setSizesOpen(open) {
        this.classList.toggle('lux-card--sizes-open', open);
        if (this.toggle) this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      step(dir) {
        if (this.imgs.length <= 1) return;
        this.imgs[this.current].classList.remove('is-active');
        this.current = (this.current + dir + this.imgs.length) % this.imgs.length;
        this.imgs[this.current].classList.add('is-active');
      }

      showMedia(mediaId) {
        const idx = this.imgs.findIndex((i) => i.dataset.mediaId === String(mediaId));
        if (idx < 0) return;
        this.imgs[this.current].classList.remove('is-active');
        this.current = idx;
        this.imgs[idx].classList.add('is-active');
      }

      variantFor(size) {
        return this.variants.find((v) => {
          const okColor = this.colorPos ? this.optOf(v, this.colorPos) === this.selectedColor : true;
          const okSize = this.sizePos ? this.optOf(v, this.sizePos) === size : true;
          return okColor && okSize;
        });
      }

      resolve(btn) {
        // variantFor() already matches the selected COLOUR (and the size when the
        // product has one). Using firstVariant() here ignored the chosen swatch on
        // colour-only products (e.g. bags), so "Add +" added the first colour.
        const v = this.variantFor(btn && btn.dataset ? btn.dataset.size : '');
        return v || this.firstVariant();
      }

      refreshSizes() {
        this.sizeBtns.forEach((btn) => {
          const v = this.resolve(btn);
          const avail = !!(v && v.available);
          btn.disabled = !avail;
          btn.classList.toggle('is-unavailable', !avail);
        });
      }

      addToCart(btn) {
        const v = this.resolve(btn);
        if (!v || !v.available) return;

        btn.classList.add('is-loading');
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        // Two-step: add first, THEN fetch a fresh cart-drawer section render (which
        // reflects the committed cart). Passing `sections` inline to add.js can
        // return the pre-add cart, leaving the drawer's item list empty until reload.
        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] }),
        })
          .then((r) => r.json())
          .then((res) => {
            btn.classList.remove('is-loading');
            if (res.status) {
              // e.g. sold out between page load and click
              btn.classList.add('is-error');
              this.refreshSizes();
              setTimeout(() => btn.classList.remove('is-error'), 1400);
              return;
            }
            btn.classList.add('is-added');
            setTimeout(() => btn.classList.remove('is-added'), 1500);
            this.setSizesOpen(false); // collapse the size bar once added
            this.refreshCart(v.id);
          })
          .catch(() => btn.classList.remove('is-loading'));
      }

      // Re-render the cart the Dawn way AND notify Dawn + any third-party cart apps
      // (upsell, free-gift, "spend $X for free shipping" bars, subscriptions) via the
      // 'cart-update' PUB_SUB event they subscribe to — this is the contract the
      // previous custom innerHTML rewrite skipped, so app widgets never refreshed.
      // Two-step (the add already committed above): fetch the fresh section HTML +
      // the committed cart, publish, then let Dawn's <cart-drawer> render it so app
      // hooks fire and every cart-count bubble (not just the header one) updates.
      refreshCart(variantId) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const drawerEl = document.querySelector('cart-drawer');
        const sectionIds =
          drawerEl && typeof drawerEl.getSectionsToRender === 'function'
            ? drawerEl.getSectionsToRender().map((s) => s.id)
            : ['cart-drawer', 'cart-icon-bubble'];
        const sectionsUrl =
          window.location.pathname + '?sections=' + encodeURIComponent(sectionIds.join(','));

        Promise.all([
          fetch(sectionsUrl).then((r) => r.json()),
          fetch(root + 'cart.js').then((r) => r.json()).catch(() => null),
        ])
          .then(([sections, cart]) => {
            // pubsub.js is loaded site-wide (classic script → global `publish`).
            if (typeof publish === 'function') {
              publish('cart-update', {
                source: 'lux-card',
                productVariantId: variantId,
                cartData: cart || {},
              });
            }
            if (drawerEl && typeof drawerEl.renderContents === 'function') {
              // Clear the empty-cart flag so items show when adding from an empty cart.
              drawerEl.classList.remove('is-empty');
              drawerEl.renderContents({ id: variantId, sections: sections });
            } else {
              // No Dawn drawer (e.g. an app replaced <cart-drawer>) — the cart-update
              // event above lets the app refresh; just flag the add for anything local.
              document.dispatchEvent(new CustomEvent('lux:cart:added', { bubbles: true }));
            }
          })
          .catch(() => {});
      }
    }
  );
}
